import {flow, getEnv, getRoot, types} from "mobx-state-tree";
import ColorState from "./color/ColorState";
import EyesAndModulesState from "./eyesAndModules/EyesAndModulesState";
import LogoState from "./logo/LogoState";
import {QrConfigSelectState} from "../../elements/qrconfig/QrConfigSelectState";
import ImageAndCharacteristicsState from "./imageAndCharacteristics/ImageAndCharacteristicsState";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import FrameAndCaptionsState from "./frameAndCaption/FrameAndCaptionsState";
import {DimensionsMultiSelectState} from "../../elements/dimensions/multi-select/dimensionsMultiSelectState";
import lodash from "lodash";
import Notification from "../../../utils/Notification";
import QrPreviewState from "../qrpreview/QrPreviewState";
import PreviewManager from "../../../managers/preview/PreviewManager";
import HttpClient from "../../../services/HttpClient";
import {scannabilityCheck} from "../../../utils/scannability";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import DefaultImageConfigModel from "src/models/DefaultImageConfig";
import { DashboardStore } from "src/stores/domain/DashboardStore";
import {MediaFileSelectState} from "../../elements/mediaFiles/MediaFileSelectState";
import {MediaFile} from "../../../models/mediaFiles/MediaFileModel";
import { initLogoStore, isMediaFileEnabled } from "./utils/DesignerTemplateUtil";
import { buildMediaFileForImageConfig } from "../../../utils/imageConfigHelper";
import { barcodeFormatConstants } from "src/utils/constants";

const DesignerState = types.model('DesignerState', {
  isLoading: types.optional(types.boolean, false),
  isEditForm: types.optional(types.boolean, false),
  isTemplateForm: types.optional(types.boolean, false),
  isCMYK: types.optional(types.boolean, false),

  logoOpen: types.optional(types.boolean, true),
  logoStore: types.maybe(LogoState),
  logoType: types.optional(types.string, "logoImage"),

  eyeOpen: types.optional(types.boolean, true),
  eyeStore: types.maybe(EyesAndModulesState),

  colorOpen: types.optional(types.boolean, true),
  colorStore: types.maybe(ColorState),

  imageCharsOpen: types.optional(types.boolean, true),
  imageCharsStore: types.maybe(ImageAndCharacteristicsState),

  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  frameOpen: types.optional(types.boolean, true),
  frameAndCaptionsStore: types.maybe(FrameAndCaptionsState),
  overlayOpen: types.optional(types.boolean, false),
  isDimensionSelectVisible: types.optional(types.boolean, false),
  dimensionAll: types.optional(types.boolean, true),
  configurationName: types.optional(types.string, ''),
  isExclusivelyGroupOwned: types.optional(types.boolean, true),
  makeDefault: types.optional(types.boolean, false),
  extendConfig: types.optional(types.string, '{}'),
  qrConfigSelectStore: types.maybeNull(types.late(() => types.reference(QrConfigSelectState))),
  groupSelectStore: types.maybeNull(types.late(() => types.reference(GroupSelectState))),
  dimensionsMultiSelectStore: types.maybeNull(types.late(() => types.reference(DimensionsMultiSelectState))),
  selectedConfigUUID:types.optional(types.string, ""),
  qrPreviewStore: types.maybe(QrPreviewState),
  isDefaultDesigner: types.optional(types.boolean, true),
  barcodeFormat: types.optional(types.string, barcodeFormatConstants.QR_CODE),
  isView: types.optional(types.boolean, false),
  // dashboardStore: types.maybe(types.reference(DashboardStore)),
  mediaFileSelectStore:  types.maybe(types.late(() => types.reference(MediaFileSelectState))),
  defaultImageConfig: types.optional(types.frozen(), {}),
  v1ImageConfig: types.optional(types.frozen(), {})
}).views(self => ({
  // Convenience getter for checking if format is Data Matrix
  get isDataMatrix() {
    return self.barcodeFormat === barcodeFormatConstants.DATA_MATRIX
  },
  // Convenience getter for checking if format is QR Code
  get isQRCode() {
    return self.barcodeFormat === barcodeFormatConstants.QR_CODE
  }
})).volatile(self => ({
  selectedCallback: types.fn,
})).actions((self) => ({
  afterCreate() {
  },
  // handleModalPopUp(component,title,componentStore,size="",className = ""){
  //   self.modalComponentStore = self[componentStore];
  //   self.modalComponent = component;
  //   self.modalSize=size;
  //   self.modalClassName=className;
  //   self.modalStore.toggle()
  //   self.modalStore.handleTitle(title)
  // },
  toggleLogoOpen() {
    self.logoOpen = !self.logoOpen
  },
  toggleEyeOpen() {
    self.eyeOpen = !self.eyeOpen
  },
  toggleColorOpen() {
    self.colorOpen = !self.colorOpen
  },
  toggleFrameOpen() {
    self.frameOpen = !self.frameOpen
  },
  toggleOverlayOpen() {
    self.overlayOpen = !self.overlayOpen
  },
  toggleImageOpen() {
    self.imageCharsOpen = !self.imageCharsOpen
  },
  handleConfigNameChange(e) {
    self.configurationName = e.target.value
  },
  toggleIsExclusivelyGroupOwned(){
    self.isExclusivelyGroupOwned = !self.isExclusivelyGroupOwned
  },
  toggleDimensionAll(){
    self.dimensionAll = !self.dimensionAll
    self.toggleIsDimensionSelectVisible(!self.dimensionAll)
    self.dimensionAll ? self.dimensionsMultiSelectStore?.selectAllDimensions() : self.dimensionsMultiSelectStore?.reset()
  },
  toggleIsDimensionSelectVisible(visible){
    self.isDimensionSelectVisible = visible
  },
  setMakeDefault() {
    self.makeDefault = !self.makeDefault
  },
  setGenerateImageListener(listener) {
    self.selectedCallback = listener
  },
  selectTag(arr) {
    self.selectedTags = arr.flatMap((tag)=> tag?.uuid)
  },
  handleUpdateImage() {
    // Check if there's an existing imageConfig selected
    // Use selectedConfigUUID as the source of truth (set by hydrateEditComponent)
    // or fallback to qrConfigSelectStore.selectedConfig for dropdown selection
    const hasExistingConfig = (self.selectedConfigUUID && self.selectedConfigUUID !== "") ||
                              (self.qrConfigSelectStore?.selectedConfig != null)
    const parsedConfig = JSON.parse(self.extendConfig)

    // Ensure logo defaults are set before generating image
    // If there's a logo but missing size/margin, set defaults
    if (parsedConfig.logo && (!parsedConfig.logoPercentageWidth || !parsedConfig.logoMargin)) {
      if (!parsedConfig.logoPercentageWidth) {
        parsedConfig.logoPercentageWidth = 27
      }
      if (!parsedConfig.logoMargin) {
        parsedConfig.logoMargin = 1
      }
      self.extendConfig = JSON.stringify(parsedConfig)
    }

    self.selectedCallback(self.extendConfig)
  },
  updateExtendConfig(key, value) {
    const parsedConfig = JSON.parse(self.extendConfig)
    const updatedConfig = {...parsedConfig, ...{[key]: value}}
    self.extendConfig = JSON.stringify(updatedConfig)
  },
  deleteFormExtendConfig(key){
    const parsedConfig = JSON.parse(self.extendConfig)
    delete parsedConfig[key]
    self.extendConfig = JSON.stringify(parsedConfig)
  },
  cleanSizeAndModuleWidth() {
    // Clean up incompatible size/moduleWidth properties before saving
    // showImageSizeOrModuleWidth: true = use 'size', false = use 'moduleWidth'
    const parsedConfig = JSON.parse(self.extendConfig)

    if (self.imageCharsStore.showImageSizeOrModuleWidth === true) {
      // Using 'size' mode - remove 'moduleWidth' if present
      delete parsedConfig.moduleWidth
    } else {
      // Using 'moduleWidth' mode - remove 'size' if present
      delete parsedConfig.size
    }

    self.extendConfig = JSON.stringify(parsedConfig)
  },
  selectGroup(obj) {
    self.tagSelectStore?.tagStore?.setFilterOn(obj?.uuid);
    self.dimensionsMultiSelectStore?.getListOnGroup(obj?.uuid)
  },
  setOnSelectChanges() {
    self.colorStore.setConsumer(self.updateExtendConfig)
    self.colorStore.setDeleteKeyConsumer(self.deleteFormExtendConfig)
    self.colorStore.setConsumeExtendConfig(self.extendConfig)

    self.eyeStore.setDesignerConsumer(self.updateExtendConfig)
    self.eyeStore.setPreviewConsumer(self.handleUpdateImage)

    self.logoStore.setConsumer(self.updateExtendConfig)
    self.logoStore.setDeleteKeyConsumer(self.deleteFormExtendConfig)
    self.logoStore.setPreviewConsumer(self.handleUpdateImage)
    // self.logoStore.logoMediaFileSelectState.setConsumer(self.setMediaFile)
    // self.logoStore.backgroundMediaFileSelectState.setConsumer(self.setMediaFile)
    self.logoStore.logoMediaFile.setConsumer(self.updateExtendConfig)
    self.logoStore.logoMediaFile.setDeleteKeyConsumer(self.deleteFormExtendConfig)
    self.logoStore.backgroundMediaFile.setConsumer(self.updateExtendConfig)
    self.logoStore.backgroundMediaFile.setDeleteKeyConsumer(self.deleteFormExtendConfig)

    self.frameAndCaptionsStore.setConsumer(self.updateExtendConfig)
    self.frameAndCaptionsStore.setDeleteKeyConsumer(self.deleteFormExtendConfig)
    self.frameAndCaptionsStore.captionRows.forEach((caption) => {
      caption.setConsumer(self.updateExtendConfig)
    })
    self.imageCharsStore.setConsumer(self.updateExtendConfig)
    self.tagSelectStore?.setConsumer(self.selectTag)
    self.groupSelectStore?.setConsumer(self.selectGroup)
  },
  // setMediaFile(mediaFile, experienceFieldName) {
  //   if(mediaFile?.uuid){
  //     if(experienceFieldName === "mediaFileCode_logo"){
  //       //self.logoStore.logo = mediaFile.liveLinks?.original
  //       self.updateExtendConfig("logo", mediaFile.liveLinks?.original)
  //       // setting logo default percentage width
  //       self.logoStore.logoPercentageWidth = 27
  //       self.updateExtendConfig("logoPercentageWidth", self.logoStore.logoPercentageWidth)
  //       //self.handleUpdateImage()
  //     }else if(experienceFieldName === "mediaFileCode_background"){
  //       // self.logoStore.backgroundImage = mediaFile.liveLinks?.original
  //       self.updateExtendConfig("backgroundImage", mediaFile.liveLinks?.original)
  //       // self.handleUpdateImage()
  //     }
  //     self.handleUpdateImage()
  //   }
  // },
  hydrateDimension(dimensions = []){
    const isDimensionAll = Object.values(dimensions).every(item => lodash.isEmpty(item));
    self.dimensionAll = !isDimensionAll
    self.toggleDimensionAll()
    if(!self.dimensionAll){
      self.dimensionsMultiSelectStore.hydrateDimensions(dimensions)
    }
  },
  //isModalExperience
  hydrateEditComponent(input, isDesignerTemplateExperience = false, isClone= false, hydrateDimensions= true) {
    const configuration = JSON.parse(input?.qrConfig)
    //clear all the stores
    self.colorStore = ColorState.create({})
    self.eyeStore = EyesAndModulesState.create({})
    // get the logo media file
    let logoThumbnailUrl = ""
    let logoOriginalUrl = ""
    let logoLargeUrl = ""
    let logoSmallUrl = ""
    let logoMediumUrl = ""
    let logoFileFormat = ""
    if(input.mediaFiles?.logoMediaFile?.liveLinks?.original){
      logoThumbnailUrl = input.mediaFiles?.logoMediaFile?.liveLinks?.thumbnail
      logoOriginalUrl = input.mediaFiles?.logoMediaFile?.liveLinks?.original
      logoLargeUrl = input.mediaFiles?.logoMediaFile?.liveLinks?.large
      logoSmallUrl = input.mediaFiles?.logoMediaFile?.liveLinks?.small
      logoMediumUrl = input.mediaFiles?.logoMediaFile?.liveLinks?.medium
      logoFileFormat = input.mediaFiles?.logoMediaFile?.fileFormat
    } else if (configuration.logo && !input?.mediaFiles?.hasOwnProperty("logoMediaFile")) {
      const logoImage = configuration.logo
      if(logoImage?.includes("https://")) {
        logoThumbnailUrl = logoImage
      } else {
        logoThumbnailUrl = `data:image/${configuration.imageFormat};base64,${logoImage}`
      }
      logoFileFormat = configuration.imageFormat
    }

    // get the background media file
    let backgroundThumbnailUrl = ""
    let backgroundOriginalUrl = ""
    let backgroundLargeUrl = ""
    let backgroundSmallUrl = ""
    let backgroundMediumUrl = ""
    let backgroundFileFormat = ""
    if(input.mediaFiles?.backgroundMediaFile?.liveLinks?.original){
      backgroundThumbnailUrl = input.mediaFiles?.backgroundMediaFile?.liveLinks?.thumbnail
      backgroundOriginalUrl = input.mediaFiles?.backgroundMediaFile?.liveLinks?.original
      backgroundLargeUrl = input.mediaFiles?.backgroundMediaFile?.liveLinks?.large
      backgroundSmallUrl = input.mediaFiles?.backgroundMediaFile?.liveLinks?.small
      backgroundMediumUrl = input.mediaFiles?.backgroundMediaFile?.liveLinks?.medium
      backgroundFileFormat = input.mediaFiles?.backgroundMediaFile?.fileFormat
    } else if (configuration.backgroundImage && !input?.mediaFiles?.hasOwnProperty("backgroundMediaFile")) {
      const backgroundImage = configuration.backgroundImage
      if(backgroundImage?.includes("https://")) {
        backgroundThumbnailUrl = backgroundImage
      } else {
        backgroundThumbnailUrl = `data:image/${configuration.imageFormat};base64,${backgroundImage}`
      }
      backgroundFileFormat = configuration.imageFormat
    }

    // Get mediaFileEnabled from appConfigs
    let mediaFileEnabled = false;
    try {
      // Try to access appConfigs through different paths
      let appConfigs = null;
      // Try through mediaFileSelectStore -> store -> rootStore
      if (self.mediaFileSelectStore?.store) {
        const rootStoreViaMediaFile = getRoot(self.mediaFileSelectStore.store);
        appConfigs = rootStoreViaMediaFile?.appConfigs;
        mediaFileEnabled = isMediaFileEnabled(appConfigs);
      }
    } catch (error) {
      console.warn('Error accessing appConfigs in DesignerState hydrateEditComponent:', error);
    }

    self.logoStore = initLogoStore(self.mediaFileSelectStore, self.dimensionsMultiSelectStore, self.tagSelectStore, mediaFileEnabled);
    const logoLiveLinks = {
      original: logoOriginalUrl,
      large: logoLargeUrl,
      medium: logoMediumUrl,
      small: logoSmallUrl,
      thumbnail: logoThumbnailUrl
    }
    self.logoStore.logoMediaFile.setLiveLinks(logoLiveLinks)
    self.logoStore.logoMediaFile.fileFormat = logoFileFormat
    if(input.mediaFiles?.logoMediaFile?.name) {
      self.logoStore.logoMediaFile.name = input.mediaFiles.logoMediaFile.name
    } else {
      self.logoStore.logoMediaFile.name = ""
    }
    if(input.mediaFiles?.logoMediaFile?.mediaFileUUID) {
      self.logoStore.logoMediaFile.mediaFileUUID = input.mediaFiles.logoMediaFile.mediaFileUUID
    } else {
      self.logoStore.logoMediaFile.mediaFileUUID = ""
    }

    const backgroundLiveLinks = {
      original: backgroundOriginalUrl,
      large: backgroundLargeUrl,
      medium: backgroundMediumUrl,
      small: backgroundSmallUrl,
      thumbnail: backgroundThumbnailUrl
    }
    self.logoStore.backgroundMediaFile.setLiveLinks(backgroundLiveLinks)
    self.logoStore.backgroundMediaFile.fileFormat = backgroundFileFormat
    if(input.mediaFiles?.backgroundMediaFile?.name) {
      self.logoStore.backgroundMediaFile.name = input.mediaFiles.backgroundMediaFile.name
    } else {
      self.logoStore.backgroundMediaFile.name = ""
    }
    if(input.mediaFiles?.backgroundMediaFile?.mediaFileUUID) {
      self.logoStore.backgroundMediaFile.mediaFileUUID = input.mediaFiles.backgroundMediaFile.mediaFileUUID
    } else {
      self.logoStore.backgroundMediaFile.mediaFileUUID = ""
    }

    // Hydrate the dropdown selection states based on existing media files
    // Check if logo media file exists and set it as selected in logo dropdown
    if (input.mediaFiles?.logoMediaFile?.mediaFileUUID && logoOriginalUrl) {
      // Find the media file in the store and set it as selected
      const logoMediaFile = self.logoStore.logoMediaFileSelectState?.store?.list?.find(
        mediaFile => mediaFile.uuid === input.mediaFiles.logoMediaFile.mediaFileUUID
      );
      if (logoMediaFile) {
        self.logoStore.logoMediaFileSelectState.selected = logoMediaFile;
      }
    }

    // Check if background media file exists and set it as selected in background dropdown
    if (input.mediaFiles?.backgroundMediaFile?.mediaFileUUID && backgroundOriginalUrl) {
      const backgroundMediaFile = self.logoStore.backgroundMediaFileSelectState?.store?.list?.find(
        mediaFile => mediaFile.uuid === input.mediaFiles.backgroundMediaFile.mediaFileUUID
      );
      if (backgroundMediaFile) {
        self.logoStore.backgroundMediaFileSelectState.selected = backgroundMediaFile;
      }
    }

    self.imageCharsStore = ImageAndCharacteristicsState.create({})
    self.frameAndCaptionsStore = FrameAndCaptionsState.create({})
    self.configurationName = ''
    self.makeDefault = false

    // Initialize extendConfig with barcodeFormat
    if (input?.barcodeFormat) {
      // Use barcodeFormat from input
      self.barcodeFormat = input.barcodeFormat
      self.extendConfig = JSON.stringify({ barcodeFormat: input.barcodeFormat })
    } else {
      // New config - default to QR_CODE
      self.barcodeFormat = barcodeFormatConstants.QR_CODE
      self.extendConfig = JSON.stringify({ barcodeFormat: barcodeFormatConstants.QR_CODE })
    }

    self.isExclusivelyGroupOwned = input?.isExclusivelyGroupOwned
    // Set selectedConfigUUID if input has uuid (existing imageConfig)
    self.selectedConfigUUID = input?.uuid || ""
    //then hydrate the stores
    self.configurationName = isClone ?  "" : input?.name
    isDesignerTemplateExperience && self.groupSelectStore?.selectGroup(input?.owner?.groupUuid)
    isDesignerTemplateExperience && self.dimensionsMultiSelectStore?.getListOnGroup(input?.owner?.groupUuid);
    isDesignerTemplateExperience && self.tagSelectStore?.setSelectedTags(input?.tags?.flatMap((tag)=> tag?.uuid))
    self.logoOpen = false
    self.eyeOpen  = false
    self.colorOpen = false
    self.imageCharsOpen = false
    self.frameOpen = false
    for (const property in configuration) {
      if (self.colorStore.hasOwnProperty(property)) {
        if(property !== "backgroundColor" && configuration[property] !== "#ffffff"){
          self.colorStore[property] = configuration[property]
          self.colorOpen = true
        } else {
          self.colorStore[property] = configuration[property]
        }
      }

      if(configuration["addThreeEyeballColors"] === false){
        self.colorStore.eyeballColor = self.colorStore.ball1Color
      }
      if(configuration["addThreeEyelidColors"] === false){
        self.colorStore.eyelidColor = self.colorStore.eye1Color
      }
      if(property === "gradientType"){
        self.colorStore.addGradient = true
        self.colorOpen = true
      }
      if (self.imageCharsStore.hasOwnProperty(property)) {
        // Convert string numbers to actual numbers for number properties
        const value = configuration[property]
        if (property === 'size' || property === 'moduleWidth' || property === 'bwr') {
          self.imageCharsStore[property] = typeof value === 'string' ? parseFloat(value) : value
        } else if (property === 'resolution' || property === 'quietZone') {
          self.imageCharsStore[property] = typeof value === 'string' ? parseInt(value) : value
        } else {
          self.imageCharsStore[property] = value
        }
      }
      if (self.eyeStore.hasOwnProperty(property)) {
        self.eyeStore[property] = configuration[property]
        // convert rotation angle to (0 - 360) degrees
        if(self.eyeStore.ball1RotationAngle < 0 ) {
          self.eyeStore.ball1RotationAngle = 180 + self.eyeStore.ball1RotationAngle
        }
        if(self.eyeStore.ball2RotationAngle < 0 ) {
          self.eyeStore.ball2RotationAngle = 180 + self.eyeStore.ball2RotationAngle
        }
        if(self.eyeStore.ball3RotationAngle < 0 ) {
          self.eyeStore.ball3RotationAngle = 180 + self.eyeStore.ball3RotationAngle
        }
        // if the template changed all eyes or balls rotation, open the all eyes rotation toggle
        if(self.eyeStore.allEyeBallsRotation || self.eyeStore.allEyeLidsRotation){
          self.eyeStore.openAllEyesRotation = true
          self.eyeOpen = true
        }
        // if the template selected individual eyes, open the individual eyes list
        if(property === "changeIndividualEyes"){
          if (self.eyeStore.changeIndividualEyes === true) {
            self.eyeStore.openEye1 = true
            self.eyeStore.openEye2 = true
            self.eyeStore.openEye3 = true
          }
          self.eyeOpen = self.eyeStore.changeIndividualEyes
        }
        if (self.eyeStore.eye1Shape !== 0 || self.eyeStore.ball1Shape !== 0){
          self.eyeStore.openPrimaryEye = true
          self.eyeOpen = true
        }
        // if template has any eye rotation, open the eye rotation toggle
        if(self.eyeStore.allEyeBallsRotation === 0 || self.eyeStore.allEyeLidsRotation === 0){
          if(self.eyeStore.eye1RotationAngle !== 0 || self.eyeStore.ball1RotationAngle !== 0){
            self.eyeStore.openEyes1Rotation = true
          }
          if(self.eyeStore.eye2RotationAngle !== 0 || self.eyeStore.ball2RotationAngle !== 0){
            self.eyeStore.openEyes2Rotation = true
          }
          if(self.eyeStore.eye3RotationAngle !== 0 || self.eyeStore.ball3RotationAngle !== 0){
            self.eyeStore.openEyes3Rotation = true
          }
        }
      }

      if (self.logoStore.hasOwnProperty(property)) {
        if(property === "backgroundImage"){
          self.logoStore.openBackgroundImage = true
        }
        self.logoStore[property] = configuration[property]
        self.logoOpen = true
        if( configuration[property] === "emptyImage"){
          self.logoStore.activeTab = "2"
        }
      }
      // if(configuration.logo){
      //   self.logoStore.logo = configuration.logo
      // }
      if(configuration.imageFormat) {
        self.logoStore.logoFormat = configuration.imageFormat
        if(configuration.imageFormat === "pdf"){
          self.logoStore.pdfLogoPng = configuration.pdfLogoPng
        }
      }
      // if(configuration.fullBackground) {
      //   self.logoStore.fullBackground = configuration.fullBackground
      // }
      // if(configuration.backgroundImage) {
      //   self.logoStore.backgroundImage = configuration.backgroundImage
      // }
      // if(configuration.logoPercentageWidth) {
      //   self.logoStore.logoPercentageWidth = configuration.logoPercentageWidth
      // }
      if(configuration[property].hasOwnProperty("captionTextPosition")) {
        self.frameAndCaptionsStore.captionRows.push(configuration[property])
        self.frameAndCaptionsStore.captions = true
         if (property !== "frameColor"){self.frameOpen = true}

      }
      if(configuration.frameSides) {
        self.frameAndCaptionsStore.frameSides = configuration.frameSides
        self.frameAndCaptionsStore.frame = true
        self.frameOpen = true
      }
      if(configuration.frameColor) {
        self.frameAndCaptionsStore.frameColor = configuration.frameColor
      }
      if(!configuration.frameUseForegroundColor) {
        self.frameAndCaptionsStore.frameUseForegroundColor = configuration.frameUseForegroundColor
        self.frameOpen = true
      }
      if(configuration.backgroundModules) {
        self.frameAndCaptionsStore.backgroundModules = configuration.backgroundModules
        self.frameOpen = true
      }
      if(!configuration.modulesUseForegroundColor) {
        self.frameAndCaptionsStore.modulesUseForegroundColor = configuration.modulesUseForegroundColor
        self.frameAndCaptionsStore.backgroundModulesColor = configuration.backgroundModulesColor
        self.frameOpen = true
      }
      if(configuration.frameOrientation) {
        self.frameAndCaptionsStore.frameRotation = configuration.frameOrientation
        self.frameOpen = true
      }
      if(configuration.backgroundModules) {
        self.frameAndCaptionsStore.fillInColor = configuration.backgroundModules
        self.frameOpen = true
      }
      if(configuration.circularText) {
        self.frameAndCaptionsStore.circularText = configuration.circularText
      }
      if(configuration.frameBackgroundImage) {
        self.frameAndCaptionsStore.frameBackgroundImage = configuration.frameBackgroundImage
      }
      // check is v1 designer config
      if(configuration["isDefaultDesigner"] === false) {
        self.isDefaultDesigner = configuration["isDefaultDesigner"]
      }
      if(configuration.bwr) {
        self.imageCharsStore.bwr = configuration["bwr"]
        self.imageCharsOpen = true
      }
      if(configuration.errorCorrection || configuration.resolution || configuration.size || configuration.quietZone || configuration.unitsOfMeasure) {
        self.imageCharsOpen = true
      }
      self.updateExtendConfig(property, configuration[property])
    }

    // showImageSizeOrModuleWidth has a default of true in imageCharsStore but older saved
    // configs may not have this key. Ensure extendConfig always reflects the correct value
    // from imageCharsStore, and remove the conflicting key so the server never receives both.
    self.updateExtendConfig("showImageSizeOrModuleWidth", self.imageCharsStore.showImageSizeOrModuleWidth)
    self.cleanSizeAndModuleWidth()

    if(input?.owner?.dimensions && hydrateDimensions){
      self.hydrateDimension(input?.owner?.dimensions)
    }

  },
  fetchConfigWithID: flow(function* f(uuid) { //this function is only used for the edit template experience
    const imageConfigManager = getEnv(self).imageConfigManager
    const res = yield imageConfigManager.fetchWithId(uuid)
    self.hydrateEditComponent(res, true, false)
    // self.handleUpdateImage()
    return res

  }),
  updateConfig: flow(function* f() {
    const GroupIsSelected = !!self.groupSelectStore.selectedGroup?.uuid
    const NameIsCreated = self.configurationName !== ""
    const DimensionIsSelected = !self.isDimensionSelectVisible || !lodash.isEmpty(self.dimensionsMultiSelectStore.getTranspiledDimensions())
    const selectedTag = self.tagSelectStore.selectedTags.map((tag) => tag?.uuid)
    if (GroupIsSelected && NameIsCreated && DimensionIsSelected) {
      self.isLoading = true
      self.updateExtendConfig("addThreeEyelidColors", self.colorStore.addThreeEyelidColors)
      self.updateExtendConfig("showImageSizeOrModuleWidth", self.imageCharsStore.showImageSizeOrModuleWidth)
      // Clean up size/moduleWidth before saving
      self.cleanSizeAndModuleWidth()
      const mediaFiles = buildMediaFileForImageConfig(self.logoStore)
      const data = {
        "uuid": self.selectedConfigUUID,
        "name": self.configurationName,
        "data": self.extendConfig,
        "groupUuid": self.groupSelectStore.selectedGroup.uuid,
        "isExclusivelyGroupOwned" : self.isExclusivelyGroupOwned,
        "tags": selectedTag,
        "mediaFiles": mediaFiles,
        "barcodeFormat": self.barcodeFormat,
        ...(self.dimensionsMultiSelectStore.dimensionStore.dimensionsEnabled) && {"dimensions": self.dimensionsMultiSelectStore.getTranspiledDimensions()}
      }
      const imageConfigManager = getEnv(self).imageConfigManager
      const res = yield imageConfigManager.update(data, self.colorStore, self.defaultImageConfig, self.v1ImageConfig)
      if (res) {
        new Notification()
          .setType("success")
          .setMessage(`${self.configurationName} Designer Template updated`)
          .send();
        self.isLoading = false
         //update the config in the list
        self.qrConfigSelectStore.configStore.defaultConfiguration.find((config) => config.uuid === self.selectedConfigUUID).updateConfig(data)
        self.handleUpdateImage(self.extendConfig)
        return res
      } else {
        new Notification()
          .setType("error")
          .setMessage(`${self.configurationName} Designer Template failed updating`)
          .send();
        self.isLoading = false
      }
    }else{
      !GroupIsSelected && new Notification()
        .setType("error")
        .setMessage("Please select a group")
        .send();
      !NameIsCreated && new Notification()
        .setType("error")
        .setMessage("Please create a name")
        .send();
      !DimensionIsSelected  && new Notification()
        .setType("error")
        .setMessage("Please select Dimensions")
        .send();
    }

  }),
  addConfig: flow(function* f(qrPreviewStore) {
    const GroupIsSelected = !!self.groupSelectStore.selectedGroup?.uuid
    const DimensionIsSelected = !self.isDimensionSelectVisible || !lodash.isEmpty(self.dimensionsMultiSelectStore.getTranspiledDimensions())
    if (GroupIsSelected && DimensionIsSelected) {
      self.isLoading = true
      self.updateExtendConfig("addThreeEyelidColors", self.colorStore.addThreeEyelidColors)
      self.updateExtendConfig("showImageSizeOrModuleWidth", self.imageCharsStore.showImageSizeOrModuleWidth)
      // Clean up size/moduleWidth before saving
      self.cleanSizeAndModuleWidth()
      const resp = yield self.qrConfigSelectStore.configStore.addConfig(self)
      if (resp >= 200 && resp < 300) {
        new Notification()
          .setType("success")
          .setMessage(`${self.configurationName} Designer Template created`.trimStart())
          .send();
        self.isLoading = false
        return resp
      } else {
        new Notification()
          .setType("error")
          .setMessage(`${self.configurationName} Designer Template failed creating. ${resp?.error}`.trimStart())
          .send();
        self.isLoading = false
        return resp
      }
    } else {
      !GroupIsSelected && new Notification()
        .setType("error")
        .setMessage("Please select a group")
        .send();
      !DimensionIsSelected && new Notification()
        .setType("error")
        .setMessage("Please select Dimensions")
        .send();
    }
  }),
  handleQrPreview(qrPreviewState) {
    self.qrPreviewStore = qrPreviewState
  },
  toggleIsDefaultDesigner(){
    self.isDefaultDesigner = !self.isDefaultDesigner
    self.updateExtendConfig("isDefaultDesigner", self.isDefaultDesigner)
  },
  toggleBarcodeFormat(){
    // Toggle between QR_CODE and DATA_MATRIX
    const newFormat = self.barcodeFormat === barcodeFormatConstants.QR_CODE
      ? barcodeFormatConstants.DATA_MATRIX
      : barcodeFormatConstants.QR_CODE

    // Save current ImageAndCharacteristics values and colorMode/foregroundColor before reset
    const savedImageChars = {
      imageFormat: self.imageCharsStore.imageFormat,
      size: self.imageCharsStore.size,
      moduleWidth: self.imageCharsStore.moduleWidth,
      showImageSizeOrModuleWidth: self.imageCharsStore.showImageSizeOrModuleWidth,
      unitsOfMeasure: self.imageCharsStore.unitsOfMeasure,
      resolution: self.imageCharsStore.resolution,
      errorCorrection: self.imageCharsStore.errorCorrection,
      quietZone: self.imageCharsStore.quietZone,
      bwr: self.imageCharsStore.bwr
    }
    const savedColorMode = self.colorStore.colorMode
    const savedForegroundColor = self.colorStore.foregroundColor

    // Reset only colorStore, eyeStore, logoStore, frameAndCaptionsStore
    // Don't reset imageCharsStore
    self.colorStore = ColorState.create({})
    self.eyeStore = EyesAndModulesState.create({})

    let mediaFileEnabled = false;
    try {
      let appConfigs = null;
      if (self.mediaFileSelectStore?.store) {
        const rootStoreViaMediaFile = getRoot(self.mediaFileSelectStore.store);
        appConfigs = rootStoreViaMediaFile?.appConfigs;
        mediaFileEnabled = isMediaFileEnabled(appConfigs);
      }
    } catch (error) {
      console.warn('Error accessing appConfigs in toggleBarcodeFormat:', error);
    }
    self.logoStore = initLogoStore(self.mediaFileSelectStore, self.dimensionsMultiSelectStore, self.tagSelectStore, mediaFileEnabled);
    self.frameAndCaptionsStore = FrameAndCaptionsState.create({})

    // Set new format
    self.barcodeFormat = newFormat

    // Restore saved color values to the colorStore
    self.colorStore.colorMode = savedColorMode
    self.colorStore.foregroundColor = savedForegroundColor

    // Build extendConfig with saved values (no isDataMatrixDesigner field)
    const baseConfig = {
      barcodeFormat: newFormat,
      colorMode: savedColorMode,
      foregroundColor: savedForegroundColor,
      ...savedImageChars
    }

    self.extendConfig = JSON.stringify(baseConfig)
  },
  reset(designerModalState) {
    self.colorStore = ColorState.create({})
    self.eyeStore = EyesAndModulesState.create({})
    let mediaFileEnabled = false;
     try {
      // Try to access appConfigs through different paths
      let appConfigs = null;
      // Try through mediaFileSelectStore -> store -> rootStore
      if (self.mediaFileSelectStore?.store) {
        const rootStoreViaMediaFile = getRoot(self.mediaFileSelectStore.store);
        appConfigs = rootStoreViaMediaFile?.appConfigs;
        mediaFileEnabled = isMediaFileEnabled(appConfigs);
      }
    } catch (error) {
      console.warn('Error accessing appConfigs in DesignerState hydrateEditComponent:', error);
    }
    self.logoStore = initLogoStore(self.mediaFileSelectStore, self.dimensionsMultiSelectStore, self.tagSelectStore, mediaFileEnabled);
    self.imageCharsStore = ImageAndCharacteristicsState.create({})
    self.frameAndCaptionsStore = FrameAndCaptionsState.create({})
    self.configurationName = ''
    self.makeDefault = false
    self.barcodeFormat = barcodeFormatConstants.QR_CODE
    self.extendConfig = JSON.stringify({ barcodeFormat: barcodeFormatConstants.QR_CODE })
    self.isExclusivelyGroupOwned = true
    self.selectedConfigUUID = ""
    self.isDefaultDesigner = true

    self.isLoading = false
  },
  resetExtendConfig() {
    self.extendConfig = '{}'
  }
}))


export default DesignerState;


